import type { ReactNode } from "react";
import { History, Route, ScrollText, ShieldCheck, Trash2, Users, Wallet, Workflow } from "lucide-react";
import type { SessionResponse } from "@amber/access/contracts";
import { AppLink } from "@amber/ui";
import { AppFrame } from "../../shell/AppFrame";
import { usePageTitle } from "../../shell/title";

export type SettingsSection = "users" | "budgets" | "history" | "pipeline" | "checks" | "routes" | "deleted" | "audit";

const SECTIONS: readonly { key: SettingsSection; label: string; icon: ReactNode }[] = [
  { key: "users", label: "Users and roles", icon: <Users size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "budgets", label: "Budgets and limits", icon: <Wallet size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "history", label: "History window", icon: <History size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "pipeline", label: "Pipeline", icon: <Workflow size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "checks", label: "Checks and policies", icon: <ShieldCheck size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "routes", label: "Routes", icon: <Route size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "deleted", label: "Deleted items", icon: <Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /> },
  { key: "audit", label: "Audit log", icon: <ScrollText size={15} strokeWidth={1.8} aria-hidden="true" /> },
];

export function SettingsPage({
  section,
  children,
}: {
  section: SettingsSection;
  children: (session: SessionResponse) => ReactNode;
}) {
  const label = SECTIONS.find((s) => s.key === section)?.label ?? "Settings";
  usePageTitle(label, "Settings");
  return (
    <AppFrame
      view="Settings"
      status={<span className="max-narrow:hidden">Every manual action is recorded in the audit log</span>}
    >
      {(session) => (
        <div className="flex min-h-0 flex-1 max-compact:flex-col">
          <nav
            aria-label="Settings"
            className="w-sidebar flex-none border-r border-divider bg-bg px-2.5 py-3.5 max-compact:w-auto max-compact:border-r-0 max-compact:border-b max-compact:py-1.5"
          >
            <h2 className="m-0 mb-2 px-2 text-xs font-semibold tracking-[0.06em] text-muted uppercase max-compact:sr-only">
              Settings
            </h2>
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0 max-compact:flex-row max-compact:overflow-x-auto">
              {SECTIONS.map((s) => (
                <li key={s.key}>
                  <AppLink
                    href={`/settings/${s.key}`}
                    aria-current={s.key === section ? "page" : undefined}
                    className="flex h-8 items-center gap-2.5 rounded-lg px-2 whitespace-nowrap text-fg no-underline hover:bg-surface-2 aria-[current=page]:bg-surface-3 aria-[current=page]:font-strong [&_svg]:text-muted"
                  >
                    {s.icon}
                    {s.label}
                  </AppLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="relative flex min-h-0 min-w-0 flex-1 flex-col bg-surface">{children(session)}</div>
        </div>
      )}
    </AppFrame>
  );
}
